import { useCallback } from 'react';

interface PostcodeRules {
  [countryCode: string]: RegExp;
}
const useValidate = () => {
  const validateTextExists = useCallback((name: string) => name.length >= 1, []);

  const validateMin2Chars = useCallback((name: string) => name.length >= 2, []);

  const validateDOB = useCallback((currentDateISO: string): boolean => {
    if (currentDateISO.length == 0 || currentDateISO == '0000-00-00') return true;
    try {
      let minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - 100);
      const minDateISO = minDate.toISOString().split('T')[0];

      let maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() - 18);
      const maxDateISO = maxDate.toISOString().split('T')[0];

      if (currentDateISO > maxDateISO || currentDateISO < minDateISO) {
        return false;
      }
      return true;
    } catch (e) {
      return true;
    }
  }, []);

  const validatePassword = useCallback((password: string) => {
    const passwordRules = new RegExp('((?=.{8,})(?=.*[A-Z]))');
    return passwordRules.test(password);
  }, []);

  const removeSpaces = (inputString: string) => {
    return inputString.replace(/\s/g, '');
  };

  const validatePhoneNumeric = useCallback((phoneNumber: string) => {
    const phoneNumberRules = /^\+?\d{10,15}$/;
    return phoneNumberRules.test(removeSpaces(phoneNumber));
  }, []);

  const validateEmail = useCallback((email: string) => {
    const emailRules =
      /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
    return emailRules.test(email);
  }, []);

  const validatePostalCode = useCallback((postalCode: string, countryCode: string) => {
    const postcodeRules: PostcodeRules = {
      AU: /^\d{4}$/,
      AT: /^\d{4}$/,
      BE: /^\d{4}$/,
      CZ: /^\d{5}$/,
      DK: /^\d{4}$/,
      FI: /^\d{5}$/,
      FR: /^\d{5}$/,
      DE: /^\d{5}$/,
      GG: /^GY\d[A-Z\d]? ?\d[A-Z]{2}$/i,
      HU: /^\d{4}$/,
      IS: /^\d{3}$/,
      IE: /^[A-Z][0-9A-Za-z]{1,2}\s?[0-9A-Za-z]{4}$/i,
      IT: /^\d{5}$/,
      JE: /^JE\d[A-Za-z\d]? ?\d[A-Za-z]{2}$/i,
      LU: /^\d{4}$/,
      NL: /^(?:NL-)?(\d{4})\s*([A-Z]{2})$/,
      NZ: /^\d{4}$/,
      PL: /^\d{5}$/,
      PT: /^\d{4}-\d{3}$/,
      SK: /^\d{5}$/,
      ES: /^\d{5}$/,
      SE: /^\d{5}$/,
      CH: /^\d{4}$/,
      GB: /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/,
    };
    try {
      const postcodeRule = postcodeRules[countryCode];
      let postCodeValid = postcodeRule.test(postalCode);
      if (postalCode.startsWith('BT') && countryCode !== 'GB') {
        postCodeValid = false;
      }
      return postCodeValid;
    } catch (e) {
      return false;
    }
  }, []);

  return {
    validatePassword,
    validatePhoneNumeric,
    validateEmail,
    validateTextExists,
    validatePostalCode,
    validateMin2Chars,
    validateDOB,
  };
};

export default useValidate;
